import { useState, useEffect } from "react";
import { Menu, X, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/armando-logo.png";
import logoBlack from "@/assets/armando-logo-black.png";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
      if (window.scrollY === 0) {
        setHasScrolled(false);
      } else if (scrolled && !hasScrolled) {
        setHasScrolled(true);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasScrolled]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Calcola l'offset per tenere conto dell'header
      const headerHeight = 80; // h-20 = 80px
      const elementTop = element.offsetTop;
      const offsetPosition = elementTop - headerHeight - 20; // 20px extra di padding
      
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
      
      setIsMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 w-full z-[9999] transition-all duration-300 ${
        isScrolled 
          ? "bg-background/95 backdrop-blur-md shadow-lg" 
          : "bg-transparent"
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className={`flex-shrink-0 transition-opacity duration-300 ${
            isScrolled ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}>
            <img
              src={logoBlack}
              alt="Da Armando Logo"
              className="h-14 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection("home")}
              className={`transition-colors duration-200 ${
                isScrolled ? "text-foreground hover:text-gold" : "text-white hover:text-white/80"
              }`}
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection("chi-siamo")}
              className={`transition-colors duration-200 ${
                isScrolled ? "text-foreground hover:text-gold" : "text-white hover:text-white/80"
              }`}
            >
              Chi Siamo
            </button>
            <button
              onClick={() => scrollToSection("menu")}
              className={`transition-colors duration-200 ${
                isScrolled ? "text-foreground hover:text-gold" : "text-white hover:text-white/80"
              }`}
            >
              Menu
            </button>
            <button
              onClick={() => scrollToSection("galleria")}
              className={`transition-colors duration-200 ${
                isScrolled ? "text-foreground hover:text-gold" : "text-white hover:text-white/80"
              }`}
            >
              Galleria
            </button>
            <button
              onClick={() => scrollToSection("dove-siamo")}
              className={`transition-colors duration-200 ${
                isScrolled ? "text-foreground hover:text-gold" : "text-white hover:text-white/80"
              }`}
            >
              Dove Siamo
            </button>
            <button
              onClick={() => scrollToSection("contatti")}
              className={`transition-colors duration-200 ${
                isScrolled ? "text-foreground hover:text-gold" : "text-white hover:text-white/80"
              }`}
            >
              Contatti
            </button>
          </nav>

          {/* Contact Info - Desktop */}
          <div className="hidden lg:flex items-center space-x-4">
            <div className={`flex items-center space-x-2 text-sm transition-colors duration-300 ${
              isScrolled ? "text-muted-foreground" : "text-white"
            }`}>
              <Phone size={16} />
              <span>+39 075 123 4567</span>
            </div>
            <Button
              onClick={() => scrollToSection("contatti")}
              style={
                isScrolled
                  ? {}
                  : {
                      backgroundColor: '#D4AF37',
                      color: '#000000',
                      borderColor: '#D4AF37'
                    }
              }
              className={`text-lg px-8 py-4 rounded-xl font-semibold transform hover:scale-105 transition-all duration-300 border-2 ${
                isScrolled
                  ? "bg-primary text-gold hover:bg-primary/90 border-gold"
                  : "border-gold"
              }`}
            >
              Prenota Ora
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden p-2 ${
              isScrolled
                ? "text-foreground hover:text-gold"
                : "text-white hover:text-white/80"
            }`}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-background/95 backdrop-blur-md border-t border-border shadow-lg">
            <nav className="flex flex-col space-y-4 py-6 px-4">
              <button 
                onClick={() => scrollToSection("home")}
                className="text-left text-gold hover:text-gold/80 transition-colors"
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection("chi-siamo")}
                className="text-left text-gold hover:text-gold/80 transition-colors"
              >
                Chi Siamo
              </button>
              <button 
                onClick={() => scrollToSection("menu")}
                className="text-left text-gold hover:text-gold/80 transition-colors"
              >
                Menu
              </button>
              <button 
                onClick={() => scrollToSection("galleria")}
                className="text-left text-gold hover:text-gold/80 transition-colors"
              >
                Galleria
              </button>
              <button 
                onClick={() => scrollToSection("dove-siamo")}
                className="text-left text-gold hover:text-gold/80 transition-colors"
              >
                Dove Siamo
              </button>
              <button 
                onClick={() => scrollToSection("contatti")}
                className="text-left text-gold hover:text-gold/80 transition-colors"
              >
                Contatti
              </button>
              <div className="pt-4 border-t border-border">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-4">
                  <Phone size={16} />
                  <span>+39 075 123 4567</span>
                </div>
                <Button 
                  onClick={() => scrollToSection("contatti")}
                  className="btn-hero w-full"
                >
                  Prenota Ora
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;